import {
  CurrencyIcon,
  HStack,
  IndicatorBadge,
  IndicatorBadgeSizes,
  IndicatorBadgeVariants,
  InlineFormattedNumber,
  Stack,
  Text,
} from '@talos/kyoko';
import { isFlatRate, rateSignVariant } from './fundingRateSign';
import { FUNDING_RATE_DISPLAY_INCREMENT, FUNDING_RATE_SETTLES_LABEL, OPEN_INTEREST_NUMBER_PROPS } from './tokens';

interface FundingRateCellTooltipProps {
  symbol: string;
  exchangeDisplayName: string | undefined;
  fundingRatePct: number;
  openInterestUsd: number | null;
  /** Current toolbar update-mode, resolved to its display label (e.g. "1H"/"1D"). */
  periodLabel: string;
}

/**
 * Hover card shown on a rate cell (List "Current Rate" column or a Matrix heat chip): identifies the
 * asset/exchange, then the current rate, the update-mode period, the metric's fixed settlement
 * cadence, and open interest.
 */
export function FundingRateCellTooltip({
  symbol,
  exchangeDisplayName,
  fundingRatePct,
  openInterestUsd,
  periodLabel,
}: FundingRateCellTooltipProps) {
  const flat = isFlatRate(fundingRatePct);
  return (
    <Stack gap="spacing12" minWidth="200px" data-testid="funding-rate-cell-tooltip-content">
      <HStack gap="spacing4" alignItems="center" justifyContent="flex-start">
        <CurrencyIcon currency={symbol} size={16} colorful />
        <Text weight="fontWeightMedium">{symbol}</Text>
        {exchangeDisplayName && <Text color="colorTextSubtle">{exchangeDisplayName}</Text>}
      </HStack>
      <Stack gap="spacing8">
        <HStack justifyContent="space-between" gap="spacing8">
          <Text color="colorTextSubtle">Current Rate</Text>
          <InlineFormattedNumber
            number={fundingRatePct}
            increment={FUNDING_RATE_DISPLAY_INCREMENT}
            currency="%"
            variant={flat ? undefined : rateSignVariant(fundingRatePct)}
          />
        </HStack>
        <HStack justifyContent="space-between" gap="spacing8">
          <Text color="colorTextSubtle">Period</Text>
          <IndicatorBadge size={IndicatorBadgeSizes.Compact} variant={IndicatorBadgeVariants.Information}>
            {periodLabel}
          </IndicatorBadge>
        </HStack>
        <HStack justifyContent="space-between" gap="spacing8">
          <Text color="colorTextSubtle">Settles</Text>
          <Text>{FUNDING_RATE_SETTLES_LABEL}</Text>
        </HStack>
        <HStack justifyContent="space-between" gap="spacing8">
          <Text color="colorTextSubtle">Open Interest</Text>
          {openInterestUsd == null ? (
            <Text color="colorTextSubtle">—</Text>
          ) : (
            <InlineFormattedNumber number={openInterestUsd} {...OPEN_INTEREST_NUMBER_PROPS} />
          )}
        </HStack>
      </Stack>
    </Stack>
  );
}
